import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '@/utils/logger';

// Available Claude models for AI web search
export interface AIModel {
  id: string;
  name: string;
  description: string;
  tier: 'fast' | 'balanced' | 'powerful';
}

export const AI_MODELS: AIModel[] = [
  {
    id: 'claude-3-haiku-20240307',
    name: 'Haiku',
    description: 'Fast & economical',
    tier: 'fast',
  },
  {
    id: 'claude-sonnet-4-20250514',
    name: 'Sonnet 4',
    description: 'Balanced (recommended)',
    tier: 'balanced',
  },
  {
    id: 'claude-3-5-sonnet-20241022',
    name: 'Sonnet 3.5',
    description: 'Previous generation',
    tier: 'balanced',
  },
  {
    id: 'claude-opus-4-20250514',
    name: 'Opus 4',
    description: 'Most powerful',
    tier: 'powerful',
  },
];

interface AIModelContextType {
  selectedModel: AIModel;
  setSelectedModel: (model: AIModel) => void;
  models: AIModel[];
}

const AIModelContext = createContext<AIModelContextType | undefined>(undefined);

const STORAGE_KEY = 'ai-model-preference';
const DEFAULT_MODEL = AI_MODELS.find(m => m.id === 'claude-sonnet-4-20250514') || AI_MODELS[1];

interface AIModelProviderProps {
  children: ReactNode;
}

export const AIModelProvider: React.FC<AIModelProviderProps> = ({ children }) => {
  const [selectedModel, setSelectedModelState] = useState<AIModel>(() => {
    // Try to load from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const modelId = JSON.parse(stored);
        const found = AI_MODELS.find(m => m.id === modelId);
        if (found) return found;
      }
    } catch (e) {
      logger.warn('Failed to load AI model preference:', e);
    }
    return DEFAULT_MODEL;
  });

  const setSelectedModel = (model: AIModel) => {
    setSelectedModelState(model);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(model.id));
    } catch (e) {
      logger.warn('Failed to save AI model preference:', e);
    }
  };

  return (
    <AIModelContext.Provider
      value={{
        selectedModel,
        setSelectedModel,
        models: AI_MODELS,
      }}
    >
      {children}
    </AIModelContext.Provider>
  );
};

export const useAIModel = (): AIModelContextType => {
  const context = useContext(AIModelContext);
  if (!context) {
    throw new Error('useAIModel must be used within an AIModelProvider');
  }
  return context;
};

export default AIModelContext;
